<template>
  <div class="maintenance-detail">
    <AppHeader
      :title="maintenance?.description || 'Maintenance Details'"
      :back-link="{ name: 'Maintenance' }"
    />

    <div v-if="loading" class="text-center py-12">
      <LoadingSpinner class="mx-auto" />
      <p class="text-gray-600 mt-2">Loading maintenance details...</p>
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <p class="text-red-800 mb-4">{{ error }}</p>
      <button @click="fetchMaintenance" class="btn-secondary">Try Again</button>
    </div>

    <div v-else-if="maintenance" class="max-w-4xl mx-auto">
      <!-- Header Card -->
      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4"
        >
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ maintenance.description }}</h1>
            <p class="text-gray-600">
              {{ maintenance.equipment?.name }} • {{ maintenance.equipment?.type }}
            </p>
          </div>
          <div class="flex space-x-2">
            <RouterLink :to="`/maintenance/edit/${maintenance.id}`" class="btn-secondary">
              Edit
            </RouterLink>
            <button @click="handleDelete" class="btn-danger">Delete</button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="text-center p-4 bg-gray-50 rounded-lg">
            <div class="text-2xl font-bold text-blue-600 mb-1">
              <MaintenanceTypeBadge :type="maintenance.maintenance_type" />
            </div>
            <p class="text-sm text-gray-600">Type</p>
          </div>

          <div class="text-center p-4 bg-gray-50 rounded-lg">
            <div class="text-2xl font-bold text-green-600 mb-1">
              {{ formatDate(maintenance.maintenance_date) }}
            </div>
            <p class="text-sm text-gray-600">Maintenance Date</p>
          </div>

          <div class="text-center p-4 bg-gray-50 rounded-lg">
            <div class="text-2xl font-bold text-purple-600 mb-1">
              {{ formatCurrency(maintenance?.cost) }}
            </div>
            <p class="text-sm text-gray-600">Cost</p>
          </div>
        </div>
      </div>

      <!-- Details Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Equipment Details -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold mb-4">Equipment Information</h3>
          <div class="space-y-3">
            <DetailItem label="Name" :value="maintenance.equipment?.name" />
            <DetailItem label="Type" :value="maintenance.equipment?.type" />
            <DetailItem label="Model" :value="maintenance.equipment?.model" />
            <DetailItem label="Serial Number" :value="maintenance.equipment?.serial_number" />
            <DetailItem label="Status" :value="maintenance.equipment?.status" />
          </div>
        </div>

        <!-- Maintenance Details -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold mb-4">Maintenance Details</h3>
          <div class="space-y-3">
            <DetailItem label="Technician" :value="maintenance.technician || 'Not specified'" />
            <DetailItem label="Scheduled Date" :value="formatDate(maintenance.maintenance_date)" />
            <DetailItem
              label="Next Due Date"
              :value="
                maintenance.next_due_date ? formatDate(maintenance.next_due_date) : 'Not scheduled'
              "
            />
            <DetailItem
              label="Payment Source"
              :value="
                maintenance.payment_method === 'LIPA_NAMBA'
                  ? 'Company Lipa Namba'
                  : maintenance.payment_method === 'BANK_ACCOUNT'
                  ? 'Company Bank Account'
                  : 'Cash Money'
              "
            />
            <DetailItem label="Created On" :value="formatDateTime(maintenance.created_at)" />
          </div>
        </div>
      </div>

      <!-- Notes and Additional Information -->
      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 class="text-lg font-semibold mb-4">Notes & Additional Information</h3>
        <div v-if="maintenance.notes" class="prose prose-sm max-w-none">
          <p class="text-gray-700">{{ maintenance.notes }}</p>
        </div>
        <p v-else class="text-gray-500 italic">No additional notes provided.</p>
      </div>

      <!-- Related Maintenance -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-lg font-semibold mb-4">Related Maintenance</h3>
        <div v-if="relatedMaintenance.length === 0" class="text-center text-gray-500 py-4">
          No related maintenance records found
        </div>
        <div v-else class="space-y-3">
          <div
            v-for="item in relatedMaintenance"
            :key="item.id"
            class="border-l-4 pl-4 py-2 cursor-pointer hover:bg-gray-50"
            :class="getMaintenanceBorderClass(item.maintenance_type)"
            @click="navigateToMaintenance(item.id)"
          >
            <p class="font-medium">{{ item.description }}</p>
            <p class="text-sm text-gray-600">{{ formatDate(item.maintenance_date) }}</p>
            <p class="text-xs text-gray-500">{{ item.maintenance_type }}</p>
          </div>
        </div>
      </div>
    </div>

    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Delete Maintenance Record"
      message="Are you sure you want to delete this maintenance record? This action cannot be undone."
      @confirm="confirmDelete"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMaintenanceStore } from '@/stores/maintenanceStore'
import { storeToRefs } from 'pinia'
import AppHeader from '@/components/shared/AppHeader.vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import MaintenanceTypeBadge from '@/components/maintenance/MaintenanceTypeBadge.vue'
import DetailItem from '@/components/shared/DetailItem.vue'
import { formatCurrency } from '@/utils/format'

const route = useRoute()
const router = useRouter()
const maintenanceStore = useMaintenanceStore()

const maintenanceId = parseInt(route.params.id as string)
const showDeleteConfirm = ref(false)

const { loading, error } = storeToRefs(maintenanceStore)

// Guard against invalid or missing ID
if (isNaN(maintenanceId)) {
  router.push('/maintenance')
} else {
  onMounted(() => {
    fetchMaintenance()
  })
}

const maintenance = computed(() => maintenanceStore.currentMaintenance)
const relatedMaintenance = computed(() =>
  maintenanceStore.maintenance
    .filter(
      (m) => m.equipment_id === maintenance.value?.equipment_id && m.id !== maintenance.value?.id,
    )
    .slice(0, 5),
)

const fetchMaintenance = () => {
  maintenanceStore.fetchMaintenanceById(maintenanceId)
}

const handleDelete = () => {
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  try {
    await maintenanceStore.deleteMaintenance(maintenanceId)
    router.push('/maintenance')
  } catch (err) {
    console.error('Failed to delete maintenance:', err)
  }
}

const navigateToMaintenance = (id: number) => {
  router.push(`/maintenance/${id}`)
}

const getMaintenanceBorderClass = (type: string) => {
  switch (type) {
    case 'scheduled':
      return 'border-blue-400'
    case 'repair':
      return 'border-red-400'
    case 'inspection':
      return 'border-green-400'
    default:
      return 'border-gray-400'
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}
</script>
